import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Braces, Layers, LayoutGrid, ListTree } from 'lucide-react';
import { ROUTES } from '@/shared/config/routes';

/**
 * The four building blocks of the taxonomy module, as deep-linkable tabs:
 *
 *  - **Schemas** — the naming schemas themselves.
 *  - **Categories** — groupings for fields: name, icon and rail position.
 *  - **Fields**  — the reusable field library (COPY-on-add templates).
 *  - **Lists**   — shared controlled vocabularies (LIVE references that propagate).
 *
 * Real path segments rather than a query param, so a tab can be linked and
 * bookmarked. `end` on Schemas stops it staying active on the child routes.
 */
/**
 * Rendered as a DIRECT child of `ListPageLayout` — a sibling sitting between the
 * header card and `ListPageBody` — so it reads as its own surface. It carries
 * the same `rounded-lg bg-card` treatment as `ListPageBody`, and the layout's
 * `gap-4` provides the separation. Nesting it inside `ListPageBody` would put a
 * white card on a white card, which is what it must not be.
 */
export function TaxonomyTabs() {
  const { t } = useTranslation('knowledge');
  const tabs = [
    {
      to: ROUTES.KNOWLEDGE_HUB_TAXONOMY,
      label: t('taxonomy.taxonomyTabs.schemas'),
      icon: Layers,
      end: true,
    },
    {
      to: ROUTES.KNOWLEDGE_HUB_TAXONOMY_CATEGORIES,
      label: t('taxonomy.taxonomyTabs.categories'),
      icon: LayoutGrid,
      end: false,
    },
    {
      to: ROUTES.KNOWLEDGE_HUB_TAXONOMY_FIELDS,
      label: t('taxonomy.taxonomyTabs.fields'),
      icon: Braces,
      end: false,
    },
    {
      to: ROUTES.KNOWLEDGE_HUB_TAXONOMY_LISTS,
      label: t('taxonomy.taxonomyTabs.lists'),
      icon: ListTree,
      end: false,
    },
  ];
  return (
    <nav
      className="shrink-0 rounded-lg bg-card px-3 pt-1.5 text-foreground"
      aria-label={t('taxonomy.taxonomyTabs.taxonomySections')}
    >
      <div className="flex items-center gap-1">
        {tabs.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }: { isActive: boolean }) =>
              `flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`
            }
          >
            <Icon className="size-4" aria-hidden />
            {label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

TaxonomyTabs.displayName = 'TaxonomyTabs';
